import {CalibratedCropSetting, CalibrationResult, Calibrator, GrayImage} from "./types";

/**
 * Y-axis marker detection result.
 */
interface YMarkerInfo {
    /** Bottom of first white block (red line) */
    bottom: number;
    /** Top of second white block (blue line) */
    top: number;
    /** Target block boundaries [start, end] */
    target: [number, number];
    /** First white block boundaries [start, end] */
    white1: [number, number];
    /** Second white block boundaries [start, end] */
    white2: [number, number];
}

/**
 * Field detection result for a single scanline.
 */
interface FieldInfo {
    /** First field [x, width] */
    field1: [number, number];
    /** Second field [x, width] */
    field2: [number, number];
    /** Debug boundaries information */
    bounds: {
        s1: number;
        e1: number;
        s2: number;
        e2: number;
        w0: [number, number];
        w1: [number, number];
        w2: [number, number];
    };
}

/**
 * Configuration options for LGPI calibration algorithm.
 */
export interface LGPICalibrationOptions {
    /** Column fraction for vertical sampling (0.0 to 1.0) */
    colFrac?: number;
    /** Target zone lower threshold */
    targetLo?: number;
    /** Target zone upper threshold */
    targetHi?: number;
    /** Minimum target block length */
    minHeaderHeight?: number;
    /** White pixel threshold (pixels > this value are white) */
    whiteGt?: number;
    /** Minimum white block length for Y detection */
    minWhiteLen?: number;
    /** Field pixel threshold (pixels < this value are field) */
    borderColorMin?: number;
    /** Minimum white run for sync point */
    minWhiteRun3?: number;
    /** Minimum white run for field end */
    minWhiteRun2?: number;
    /** Minimum field run length */
    minFieldRun?: number;
}

/**
 * Web port of the LGPI calibrator (detects the lastname and firstname fields
 * by analyzing pixel patterns using specific thresholds).
 */
export class LGPICalibrator implements Calibrator {
    private options: Required<LGPICalibrationOptions>;
    private slugs = ['lastname', 'firstname'];
    private scaleFactor: number = 1;

    constructor(options: LGPICalibrationOptions = {}) {
        this.options = {
            colFrac: options.colFrac ?? 0.27,
            targetLo: options.targetLo ?? 100,
            targetHi: options.targetHi ?? 125,
            minHeaderHeight: options.minHeaderHeight ?? 17,
            whiteGt: options.whiteGt ?? 250,
            minWhiteLen: options.minWhiteLen ?? 2,
            borderColorMin: options.borderColorMin ?? 200,
            minWhiteRun3: options.minWhiteRun3 ?? 3,
            minWhiteRun2: options.minWhiteRun2 ?? 2,
            minFieldRun: options.minFieldRun ?? 100,
        };
    }

    /**
     * Calibrate a screenshot and detect the two fields.
     */
    calibrate(image: GrayImage, scaleFactor: number): CalibrationResult {
        const {data: gray, width, height} = image;

        this.scaleFactor = scaleFactor;

        if (!width || !height) {
            throw new Error('Unable to read image dimensions');
        }

        // Calculate column position for Y detection
        const xCol = Math.round(this.options.colFrac * (width - 1));
        const xColClamped = Math.max(0, Math.min(width - 1, xCol));

        // Find Y markers (red and blue lines)
        const scanlines = this.findScanlines(gray, width, height, xColClamped);

        if (!scanlines) {
            throw new Error('Scanline detection failed');
        }

        // Scan line is 3 pixels below red line
        const yScan = scanlines.top + (4 * this.scaleFactor);

        if (yScan >= height || yScan >= scanlines.bottom) {
            throw new Error(`Invalid scan line y=${yScan} (height=${height}, bottom=${scanlines.bottom})`);
        }

        // Detect fields on the scanline
        const fieldInfo = this.findTwoFieldsOnScanline(gray, width, yScan);

        if (!fieldInfo) {
            throw new Error('Field detection failed on scanline');
        }

        // Use red and blue lines as field boundaries
        const topY = Math.max(0, Math.min(height - 1, scanlines.top));
        let botY = Math.max(0, Math.min(height - 1, scanlines.bottom));
        if (botY <= topY) {
            botY = Math.min(height - 1, topY + 1);
        }
        const fieldHeight = botY - topY;

        const offsetY = 3;
        const offsetX = 0;

        const cropConfig: CalibratedCropSetting[] = [
            {
                slug: this.slugs[0],
                x: (fieldInfo.field1[0] + offsetX).toString(),
                y: (topY + offsetY).toString(),
                width: (fieldInfo.field1[1] - fieldInfo.field1[0] - offsetX * 2).toString(),
                height: (fieldHeight - offsetY * 2).toString()
            },
            {
                slug: this.slugs[1],
                x: (fieldInfo.field2[0] + offsetX).toString(),
                y: (topY + offsetY).toString(),
                width: (fieldInfo.field2[1] - fieldInfo.field2[0] - offsetX * 2).toString(),
                height: (fieldHeight - offsetY * 2).toString()
            }
        ];

        return {
            config: cropConfig,
            debug: {
                xCol: xColClamped,
                scanlines,
                yScan,
                fieldInfo,
            },
        };
    }

    /**
     * Finds contiguous blocks where a predicate is true.
     *
     * @returns Array of [start, end] tuples (end is exclusive)
     */
    private contiguousBlocks(values: number[], pred: (v: number) => boolean): [number, number][] {
        const blocks: [number, number][] = [];
        let inBlock = false;
        let start = 0;

        for (let i = 0; i < values.length; i++) {
            const ok = pred(values[i]);
            if (ok && !inBlock) {
                start = i;
                inBlock = true;
            } else if (!ok && inBlock) {
                blocks.push([start, i]);
                inBlock = false;
            }
        }

        if (inBlock) {
            blocks.push([start, values.length]);
        }

        return blocks;
    }

    /**
     * Detects Y markers from a vertical column analysis.
     * Finds target zone and two white blocks to determine red and blue lines.
     */
    private findScanlines(
        gray: Uint8Array,
        width: number,
        height: number,
        xCol: number
    ): YMarkerInfo | null {
        let target: [number, number] | null = null;
        let col: number[] = [];

        for (let x = xCol; x <= xCol + 50 * this.scaleFactor; x++) {
            col = [];
            // Extract column
            for (let y = 0; y < height; y++) {
                col.push(gray[y * width + x]);
            }

            // 1) Find first target block
            const targetBlocks = this.contiguousBlocks(
                col,
                v => v >= this.options.targetLo && v <= this.options.targetHi
            );

            target = targetBlocks[0];

            if (target && (target[1] - target[0] >= (this.options.minHeaderHeight) * this.scaleFactor)) {
                break;
            }
        }

        if (!target) {
            return null;
        }

        const [ts, te] = target;

        // 2) Find next two white blocks after target
        const colAfterTarget = col.slice(te);
        const whiteBlocksAll = this.contiguousBlocks(
            colAfterTarget,
            v => v > this.options.whiteGt
        );

        const whites: [number, number][] = [];
        for (const [s, e] of whiteBlocksAll) {
            if (e - s >= this.options.minWhiteLen && e - s < 20 * this.scaleFactor) {
                whites.push([s + te, e + te]);
                if (whites.length === 2) {
                    break;
                }
            }
        }

        if (whites.length < 2) {
            return null;
        }

        const [[w1s, w1e], [w2s, w2e]] = whites;
        const top = Math.max(0, w1e - 1);  // bottom of first white zone
        const bottom = w2s;                // top of second white zone

        return {
            top,
            bottom,
            target: [ts, te],
            white1: [w1s, w1e],
            white2: [w2s, w2e]
        };
    }

    /**
     * Detects two fields on a horizontal scanline.
     * Uses white runs to synchronize and find field boundaries.
     */
    private findTwoFieldsOnScanline(
        gray: Uint8Array,
        width: number,
        yScan: number
    ): FieldInfo | null {
        // Extract row
        const row: number[] = [];
        for (let x = 0; x < width; x++) {
            row.push(gray[yScan * width + x]);
        }
        const isBorder = (v: number) => v < this.options.borderColorMin;

        const borders = this.contiguousBlocks(row, isBorder);

        if (borders[0][0] === 0) {
            borders.shift();
        }

        const fields = this.fieldsFromBorders(borders);

        if (fields.length < 2) {
            return null;
        }

        const b0s = borders[0][1];
        const b0e = borders[1][0];

        const field1Start = fields[0][0];
        const field1End = fields[0][1];

        const w1s = borders[2][1];
        const w1e = borders[3][0];

        const field2Start = fields[1][0];
        const field2End = fields[1][1];

        const w2s = borders[4][1];
        const w2e = borders[5][0];

        if (field1End >= field2Start) {
            return null;
        }

        return {
            field1: [field1Start, field1End],
            field2: [field2Start, field2End],
            bounds: {
                s1: field1Start, e1: field2Start, s2: field1End, e2: field2End,
                w0: [b0s, b0e],
                w1: [w1s, w1e],
                w2: [w2s, w2e]
            }
        };
    }

    /**
     * Extracts fields from border positions.
     */
    private fieldsFromBorders(borders: number[][]): [number, number][] {
        const fields: [number, number][] = [];
        for (let i = 1; i < borders.length - 1; i++) {
            const start = borders[i][1];
            const end = borders[i + 1][0];

            if (end > start && end - start >= this.options.minFieldRun) {
                fields.push([start, end]);
            }
        }

        return fields;
    }
}
